import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Paper,
    Skeleton,
    Snackbar,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import axiosInstance from '../../helpers/axios';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import LoanHypothecationTab from './LoanHypothecationTab';

const TABS = [
    { key: 'funds', label: 'Fund Management' },
    { key: 'hypothecation', label: 'Loan Hypothecation' },
];

function formatToLakhs(amount) {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${parseFloat((amount / 10000000).toFixed(2))} Cr`;
    if (amount >= 100000) return `₹${parseFloat((amount / 100000).toFixed(2))} L`;
    return `₹${Number(amount).toLocaleString('en-IN')}`;
}

function utilizationPercent(utilized, sanctioned) {
    if (!sanctioned) return 0;
    return Math.min(100, Math.round((utilized / sanctioned) * 100));
}

function StatCard({ label, value, icon: Icon, color, bg }) {
    return (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', display: 'flex', alignItems: 'center', gap: '14px', flex: 1 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '12px', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon sx={{ fontSize: 28, color }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
                <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', lineHeight: 1.4, marginTop: '2px' }}>{label}</Typography>
            </Box>
        </Box>
    );
}

function FundTableSkeleton() {
    return (
        <TableBody>
            {Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
}

export default function FundManagement() {
    const decode = useCurrentUser();
    const hasAccess = decode?.role === 'admin' || decode?.role === 'fund_manager' || decode?.role === 'fund_user';
    const [activeTabKey, setActiveTabKey] = useState(TABS[0].key);

    const [funds, setFunds] = useState([]);
    const [stats, setStats] = useState({ totalFunds: 0, totalSanctioned: 0, totalUtilized: 0, totalLoansHypothecated: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingFund, setEditingFund] = useState(null);
    const [name, setName] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [totalSanctionedLimit, setTotalSanctionedLimit] = useState('');
    const [notes, setNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [modalError, setModalError] = useState(null);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const loadFunds = useCallback(() => {
        setIsLoading(true);
        setErrorMessage(null);
        axiosInstance
            .get('/admin-settings/funds')
            .then((response) => {
                setFunds(response.data.data.funds);
                setStats(response.data.data.stats);
            })
            .catch(() => setErrorMessage('Could not load funds.'))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        loadFunds();
    }, [loadFunds]);

    if (!hasAccess) return null;

    const availableTotal = (stats.totalSanctioned || 0) - (stats.totalUtilized || 0);

    const openCreateModal = () => {
        setEditingFund(null);
        setName('');
        setContactPerson('');
        setContactEmail('');
        setContactPhone('');
        setTotalSanctionedLimit('');
        setNotes('');
        setModalError(null);
        setModalOpen(true);
    };

    const openEditModal = (fund) => {
        setEditingFund(fund);
        setName(fund.name || '');
        setContactPerson(fund.contactPerson || '');
        setContactEmail(fund.contactEmail || '');
        setContactPhone(fund.contactPhone || '');
        setTotalSanctionedLimit(fund.totalSanctionedLimit != null ? String(fund.totalSanctionedLimit) : '');
        setNotes(fund.notes || '');
        setModalError(null);
        setModalOpen(true);
    };

    const handleSave = () => {
        setIsSaving(true);
        setModalError(null);
        const payload = {
            name,
            contactPerson: contactPerson || undefined,
            contactEmail: contactEmail || undefined,
            contactPhone: contactPhone || undefined,
            totalSanctionedLimit: totalSanctionedLimit ? Number(totalSanctionedLimit) : undefined,
            notes: notes || undefined,
        };
        const request = editingFund
            ? axiosInstance.put(`/admin-settings/funds/${editingFund.id}`, payload)
            : axiosInstance.post('/admin-settings/funds', payload);

        request
            .then(() => {
                setModalOpen(false);
                setSnackbar({
                    open: true,
                    message: editingFund ? 'Fund updated successfully' : 'Fund created successfully',
                    severity: 'success',
                });
                loadFunds();
            })
            .catch((error) => {
                setModalError(error?.response?.data?.message || 'Could not save fund.');
            })
            .finally(() => setIsSaving(false));
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        setDeleteError(null);
        axiosInstance
            .delete(`/admin-settings/funds/${deleteTarget.id}`)
            .then(() => {
                setDeleteTarget(null);
                setSnackbar({ open: true, message: 'Fund deleted successfully', severity: 'success' });
                loadFunds();
            })
            .catch((error) => {
                if (error?.response?.status === 409) {
                    setDeleteError('Cannot delete: this fund has active hypothecated loans. Release them first.');
                } else {
                    setDeleteError(error?.response?.data?.message || 'Could not delete fund.');
                }
            });
    };

    const canSave = name.trim().length > 0;

    return (
        <div style={{ width: '100%', backgroundColor: 'white', minHeight: '100vh' }}>
            <Box sx={{ padding: '24px' }}>
                <h2>🏦 Fund Management</h2>
                <Tabs value={activeTabKey} onChange={(e, value) => setActiveTabKey(value)}>
                    {TABS.map((tab) => (
                        <Tab key={tab.key} value={tab.key} label={tab.label} disableRipple />
                    ))}
                </Tabs>

                <Box sx={{ padding: '24px 0' }}>
                    {activeTabKey === 'funds' && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' }}>
                                <StatCard label="Active Funds" value={stats.totalFunds} icon={AccountBalanceOutlinedIcon} color="#D9480F" bg="#FEF2ED" />
                                <StatCard label="Total Sanctioned" value={formatToLakhs(stats.totalSanctioned)} icon={AccountBalanceWalletOutlinedIcon} color="#2563eb" bg="#EFF6FF" />
                                <StatCard label="Total Utilized" value={formatToLakhs(stats.totalUtilized)} icon={TrendingUpOutlinedIcon} color="#d97706" bg="#FFFBEB" />
                                <StatCard label="Available" value={formatToLakhs(availableTotal)} icon={SavingsOutlinedIcon} color="#16a34a" bg="#F0FDF4" />
                                <StatCard label="Loans Hypothecated" value={stats.totalLoansHypothecated} icon={ReceiptLongOutlinedIcon} color="#0d9488" bg="#F0FDFA" />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                                <Button variant="contained" onClick={openCreateModal}>+ Add Fund</Button>
                            </Box>

                            {errorMessage && <Alert severity="error" sx={{ marginBottom: '12px' }}>{errorMessage}</Alert>}

                            <Paper variant="outlined" sx={{ borderRadius: '8px' }}>
                                <TableContainer>
                                    <Table sx={{ tableLayout: 'fixed' }}>
                                        <TableHead sx={{ backgroundColor: '#F8F8F8' }}>
                                            <TableRow>
                                                <TableCell sx={{ width: '18%' }}>Fund Name</TableCell>
                                                <TableCell sx={{ width: '15%' }}>Contact Person</TableCell>
                                                <TableCell sx={{ width: '13%' }}>Sanctioned Limit</TableCell>
                                                <TableCell sx={{ width: '17%' }}>Utilized</TableCell>
                                                <TableCell sx={{ width: '13%' }}>Available</TableCell>
                                                <TableCell sx={{ width: '9%' }}>Loans</TableCell>
                                                <TableCell sx={{ width: '15%' }} align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        {isLoading ? (
                                            <FundTableSkeleton />
                                        ) : funds.length === 0 ? (
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', padding: '32px' }}>
                                                        No funds added yet. Add your first fund partner to get started.
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        ) : (
                                            <TableBody>
                                                {funds.map((fund) => {
                                                    const pct = utilizationPercent(fund.totalHypothecatedAmount, fund.totalSanctionedLimit);
                                                    return (
                                                        <TableRow key={fund.id} hover>
                                                            <TableCell sx={{ fontWeight: 600 }}>{fund.name}</TableCell>
                                                            <TableCell>{fund.contactPerson || '—'}</TableCell>
                                                            <TableCell>{formatToLakhs(fund.totalSanctionedLimit)}</TableCell>
                                                            <TableCell>
                                                                {formatToLakhs(fund.totalHypothecatedAmount)}
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={pct}
                                                                    color={pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'success'}
                                                                    sx={{ marginTop: '4px', borderRadius: '3px', height: '6px' }}
                                                                />
                                                                <Typography sx={{ fontSize: '10px', color: '#64748b' }}>{pct}%</Typography>
                                                            </TableCell>
                                                            <TableCell>{formatToLakhs(fund.availableLimit)}</TableCell>
                                                            <TableCell>{fund.loansCount}</TableCell>
                                                            <TableCell align="right">
                                                                <IconButton size="small" onClick={() => openEditModal(fund)}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => { setDeleteTarget(fund); setDeleteError(null); }}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        )}
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Box>
                    )}

                    {activeTabKey === 'hypothecation' && <LoanHypothecationTab />}
                </Box>
            </Box>

            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editingFund ? 'Edit Fund' : 'Add New Fund'}</DialogTitle>
                <DialogContent>
                    {modalError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{modalError}</Alert>}
                    <TextField
                        fullWidth
                        label="Fund Name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        sx={{ marginBottom: '16px', marginTop: '8px' }}
                    />
                    <TextField
                        fullWidth
                        label="Contact Person"
                        value={contactPerson}
                        onChange={(e) => setContactPerson(e.target.value)}
                        sx={{ marginBottom: '16px' }}
                    />
                    <TextField
                        fullWidth
                        label="Contact Email"
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        sx={{ marginBottom: '16px' }}
                    />
                    <TextField
                        fullWidth
                        label="Contact Phone"
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        sx={{ marginBottom: '16px' }}
                    />
                    <TextField
                        fullWidth
                        label="Sanctioned Limit (₹)"
                        type="number"
                        value={totalSanctionedLimit}
                        onChange={(e) => setTotalSanctionedLimit(e.target.value)}
                        helperText="Enter amount in rupees. Example: 50000000 = ₹5 Cr"
                        sx={{ marginBottom: '16px' }}
                    />
                    <TextField
                        fullWidth
                        label="Notes"
                        multiline
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)} disabled={isSaving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={!canSave || isSaving}>
                        {isSaving ? 'Saving...' : 'Save Fund'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Delete Fund</DialogTitle>
                <DialogContent>
                    {deleteError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{deleteError}</Alert>}
                    Are you sure you want to delete fund {deleteTarget?.name}?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
